(() => {
  'use strict';

  const TEST_HOSTS = new Set(['', 'localhost', '127.0.0.1', '::1']);
  const CCT_COLORS = {
    warm: '242, 196, 135',
    neutral: '255, 255, 255',
    cool: '179, 213, 247'
  };
  const guideMark = (text, tone) => ({ text, tone });
  const MIRROR_PROMPTS = {
    supply: 'Try the wall power switch',
    power: 'Tap Power once',
    colour: 'Tap Power again for colour',
    brightness: 'when LED ON, long hold power',
    defog: 'Tap Defog',
    radar: 'Move in front of the mirror',
    idle: 'Step away for auto off',
    'sensor-pause': 'with LED OFF, long hold power to pause sensor',
    'sensor-disable': 'with LED OFF, long hold power to pause sensor',
    'sensor-restore': 'with LED OFF, first hold Power to pause sensor',
    'sensor-setting': 'long hold power again for setting mode',
    'sensor-restore-setting': 'now long hold power again for setting mode',
    'sensor-disable-confirm': 'long hold power again to confirm to disable sensor',
    'sensor-restore-tap': 'tap power once to set sensor enabled',
    'sensor-restore-confirm': 'long hold power again to confirm restore sensor'
  };
  const FEATURE_TASKS = [
    { key: 'radar', completedCounter: 'guided_sensor_completed' },
    { key: 'power', completedCounter: 'guided_power_completed' },
    { key: 'colour', completedCounter: 'guided_colour_completed' },
    { key: 'brightness', completedCounter: 'guided_brightness_completed' },
    { key: 'defog', completedCounter: 'guided_defog_completed' },
    { key: 'sensor-disable', completedCounter: 'guided_radar_disable_completed' },
    { key: 'sensor-restore', completedCounter: 'guided_radar_reactivate_completed' }
  ];
  const FEATURE_TASK_KEYS = FEATURE_TASKS.map((task) => task.key);
  const FEATURE_TASK_BY_KEY = Object.fromEntries(FEATURE_TASKS.map((task) => [task.key, task]));
  const GUIDE_COPY = {
    supply: [
      'Use the ', guideMark('red', 'danger'), ' ', guideMark('ON/OFF', 'action'),
      ' control at the upper right of the mirror to disconnect or reconnect simulated ',
      guideMark('external power', 'state'),
      '. It is not a switch on the product.'
    ],
    power: [
      'Tap ', guideMark('Power', 'power'), ' once. ', guideMark('LED ON / backlight OFF', 'state'),
      '; ', guideMark('LED OFF / backlight ON', 'state'), '.'
    ],
    colour: [
      'After tapping ', guideMark('Power', 'power'), ', tap again ',
      guideMark('within 3 seconds', 'timing'), ' of the previous tap for the ',
      guideMark('colour cycle', 'state'), '. After ', guideMark('more than 3 seconds', 'timing'),
      ', the next tap becomes ', guideMark('ON/OFF', 'state'), '.'
    ],
    brightness: [
      'Only with ', guideMark('LED ON', 'state'), ': ',
      guideMark('hold Power for 1 second', 'power'),
      '. ', guideMark('Release', 'action'), ' at the brightness you want.'
    ],
    defog: [
      guideMark('Tap Defog', 'action'), ' once to switch the ', guideMark('demister ON/OFF', 'state'), '.'
    ],
    radar: [
      guideMark('Move across the mirror', 'action'), ' to simulate motion and switch the ',
      guideMark('LED ON', 'state'), '.'
    ],
    idle: [
      'After motion is triggered, ', guideMark('no motion for 60 seconds', 'timing'),
      ' switches the ', guideMark('LED OFF', 'state'), '.'
    ],
    'sensor-pause': [
      'With ', guideMark('LED OFF', 'state'), ', ', guideMark('long hold Power', 'power'),
      ' to temporarily pause the ', guideMark('sensor', 'state'), '.'
    ],
    'sensor-disable': [
      'With ', guideMark('LED OFF', 'state'), ', ', guideMark('long hold Power 3 times', 'power'),
      ' to set ', guideMark('SENSOR DISABLED', 'state'), '.'
    ],
    'sensor-restore': [
      'From ', guideMark('LED OFF', 'state'), ': ',
      guideMark('hold Power twice, tap once, then hold once again', 'power'),
      ' to enable the ', guideMark('sensor', 'state'), '.'
    ]
  };
  const DEFAULT_SNAPSHOT = {
    format: 'tap-mirror-snapshot',
    version: 2,
    product: {
      name: 'Sensor LED Mirror',
      model: 'baseline-001-010'
    },
    timing: {
      bootSeconds: 3,
      tapCycleWindowSeconds: 3,
      brightnessHoldSeconds: 1,
      serviceHoldSeconds: 1.5,
      serviceStepTimeoutSeconds: 6,
      radarIdleSeconds: 60
    },
    light: {
      defaultBrightness: 80,
      minimumBrightness: 10,
      maximumBrightness: 150,
      defaultCct: 'neutral',
      cctCycle: ['off', 'cool', 'neutral', 'warm']
    },
    attribution: {
      brand: 'Oz Bathroom',
      storeUrl: 'https://www.ozbathroom.com.au',
      allowedDomains: [
        'www.ozbathroom.com.au',
        'ozbathroom-au.myshopify.com'
      ]
    }
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const normalizeHost = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./, '');

  function isTestEnvironment() {
    const params = new URLSearchParams(window.location.search);
    return TEST_HOSTS.has(window.location.hostname)
      || window.location.protocol === 'file:'
      || Boolean(window.Shopify?.designMode)
      || params.has('preview_theme_id');
  }

  function createState(snapshot) {
    const light = snapshot.light;
    return {
      supplyOn: true,
      booting: false,
      lightOn: false,
      brightness: light.defaultBrightness,
      cct: light.defaultCct,
      memory: {
        brightness: light.defaultBrightness,
        cct: light.defaultCct
      },
      defogOn: false,
      sensorMode: 'enabled',
      radarMotion: false,
      indicators: {
        sensor: 'white',
        defog: 'white',
        power: 'white'
      }
    };
  }

  function normalizedText(element) {
    return String(element?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function findProductLabel(root, labels) {
    const wrapper = root.closest('.shopify-section') || root;
    const productArea = root.closest('main') || document.querySelector('main') || document.body;
    const candidates = productArea.querySelectorAll(
      'summary, button, a, [role="button"], h2, h3, h4, [class*="accordion-title"], [class*="accordion__title"], [class*="tab-title"]'
    );
    return [...candidates].find((element) => {
      if (wrapper.contains(element)) return false;
      if (!element.getClientRects().length) return false;
      const text = normalizedText(element);
      return labels.some((label) => text === label || text.startsWith(`${label} `));
    }) || null;
  }

  function productBlockFor(element) {
    if (!element) return null;
    return element.closest(
      'details, product-tab, [data-block-id], .shopify-block, .product__accordion, .productView-accordion, .productView-tab, .product-block, .accordion-item, .tab-content'
    ) || element;
  }

  function configureProductPlacement(root) {
    const mobileEnabled = root.dataset.mobileProductPlacement === 'true';
    const desktopEnabled = root.dataset.desktopProductLayout === 'true';
    if ((!mobileEnabled && !desktopEnabled) || window.location.protocol === 'file:') return;

    const wrapper = root.closest('.shopify-section') || root;
    const originalParent = wrapper.parentNode;
    if (!originalParent) return;

    const wrapperPlaceholder = document.createComment('LED mirror simulator original position');
    originalParent.insertBefore(wrapperPlaceholder, wrapper);
    const mobileMedia = window.matchMedia('(max-width: 767px)');
    const desktopMedia = window.matchMedia('(min-width: 990px)');
    let observer = null;
    let stopTimer = null;
    let desktopLayout = null;
    let descriptionBlock = null;
    let shippingBlock = null;
    let descriptionPlaceholder = null;
    let shippingPlaceholder = null;

    const restoreWrapper = () => {
      if (wrapperPlaceholder.parentNode && wrapper.previousSibling !== wrapperPlaceholder) {
        wrapperPlaceholder.parentNode.insertBefore(wrapper, wrapperPlaceholder.nextSibling);
      }
      root.classList.remove('is-mobile-relocated');
      root.classList.remove('is-desktop-relocated');
    };

    const restoreDesktopBlocks = () => {
      if (descriptionPlaceholder?.parentNode && descriptionBlock) {
        descriptionPlaceholder.parentNode.insertBefore(descriptionBlock, descriptionPlaceholder.nextSibling);
      }
      if (shippingPlaceholder?.parentNode && shippingBlock) {
        shippingPlaceholder.parentNode.insertBefore(shippingBlock, shippingPlaceholder.nextSibling);
      }
      restoreWrapper();
      desktopLayout?.remove();
      descriptionPlaceholder?.remove();
      shippingPlaceholder?.remove();
      desktopLayout = null;
      descriptionBlock = null;
      shippingBlock = null;
      descriptionPlaceholder = null;
      shippingPlaceholder = null;
      root.classList.remove('is-desktop-relocated');
    };

    const restoreAll = () => {
      restoreDesktopBlocks();
      restoreWrapper();
    };

    const placeMobile = () => {
      restoreDesktopBlocks();
      restoreWrapper();
      const description = productBlockFor(findProductLabel(root, ['description']));
      if (description?.parentNode && !description.contains(wrapper)) {
        description.parentNode.insertBefore(wrapper, description);
        root.classList.add('is-mobile-relocated');
        return true;
      }

      const enquire = productBlockFor(findProductLabel(root, ['enquire now', 'inquire now']));
      if (enquire?.parentNode && !enquire.contains(wrapper)) {
        enquire.parentNode.insertBefore(wrapper, enquire.nextSibling);
        root.classList.add('is-mobile-relocated');
        return true;
      }
      return false;
    };

    const placeDesktop = () => {
      if (desktopLayout?.isConnected) return true;
      restoreAll();

      descriptionBlock = productBlockFor(findProductLabel(root, ['description']));
      shippingBlock = productBlockFor(findProductLabel(root, [
        'shipping & return',
        'shipping and return',
        'shipping & returns',
        'shipping and returns'
      ]));
      if (!descriptionBlock?.parentNode
        || !shippingBlock?.parentNode
        || descriptionBlock.contains(wrapper)
        || shippingBlock.contains(wrapper)) {
        descriptionBlock = null;
        shippingBlock = null;
        return false;
      }

      const layoutParent = descriptionBlock.parentNode;
      descriptionPlaceholder = document.createComment('Description original position');
      layoutParent.insertBefore(descriptionPlaceholder, descriptionBlock);
      if (shippingBlock !== descriptionBlock) {
        shippingPlaceholder = document.createComment('Shipping and return original position');
        shippingBlock.parentNode.insertBefore(shippingPlaceholder, shippingBlock);
      }

      desktopLayout = document.createElement('div');
      desktopLayout.className = 'led-mirror-snapshot-product-layout';
      const detailsColumn = document.createElement('div');
      detailsColumn.className = 'led-mirror-snapshot-product-layout__details';
      const simulatorColumn = document.createElement('div');
      simulatorColumn.className = 'led-mirror-snapshot-product-layout__simulator';
      desktopLayout.append(detailsColumn, simulatorColumn);
      layoutParent.insertBefore(desktopLayout, descriptionPlaceholder);
      detailsColumn.append(descriptionBlock);
      if (shippingBlock !== descriptionBlock) detailsColumn.append(shippingBlock);
      simulatorColumn.append(wrapper);
      root.classList.add('is-desktop-relocated');
      return true;
    };

    const place = () => {
      if (mobileEnabled && mobileMedia.matches) return placeMobile();
      if (desktopEnabled && desktopMedia.matches) return placeDesktop();
      restoreAll();
      return true;
    };

    const update = () => {
      if (place() || observer) return;
      observer = new MutationObserver(() => {
        if (!place()) return;
        observer.disconnect();
        observer = null;
      });
      observer.observe(document.body, { childList: true, subtree: true });
      stopTimer = window.setTimeout(() => {
        observer?.disconnect();
        observer = null;
      }, 10000);
    };

    mobileMedia.addEventListener?.('change', update);
    desktopMedia.addEventListener?.('change', update);
    update();

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target?.contains(root)) return;
      mobileMedia.removeEventListener?.('change', update);
      desktopMedia.removeEventListener?.('change', update);
      observer?.disconnect();
      if (stopTimer) window.clearTimeout(stopTimer);
      restoreAll();
      wrapperPlaceholder.remove();
    });
  }

  class EngagementTracker {
    constructor(root, snapshot) {
      this.root = root;
      this.snapshot = snapshot;
      this.endpoint = String(root.dataset.analyticsEndpoint || '').trim();
      this.anonKey = String(root.dataset.supabaseAnonKey || '').trim();
      this.sessionId = crypto.randomUUID?.() || `mirror-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.startedAt = null;
      this.lastActiveAt = null;
      this.activeMilliseconds = 0;
      this.lastFlushAt = 0;
      this.flushTimer = null;
      this.counters = {
        supply_toggles: 0,
        power_taps: 0,
        defog_taps: 0,
        brightness_adjustments: 0,
        colour_changes: 0,
        sensor_triggers: 0,
        service_actions: 0,
        service_starts: 0,
        radar_disable_actions: 0,
        radar_reactivate_actions: 0,
        guided_sensor_completed: 0,
        guided_power_completed: 0,
        guided_colour_completed: 0,
        guided_brightness_completed: 0,
        guided_defog_completed: 0,
        guided_radar_pause_completed: 0,
        guided_radar_disable_completed: 0,
        guided_radar_reactivate_completed: 0
      };
      this.feedbackForm = root.querySelector('[data-feedback-form]');
      this.feedbackStatus = root.querySelector('[data-feedback-status]');
      this.bind();
    }

    analyticsAllowed() {
      try {
        const privacy = window.Shopify?.customerPrivacy;
        return !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
      } catch {
        return false;
      }
    }

    bind() {
      this.feedbackForm?.addEventListener('submit', (event) => this.submitFeedback(event));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
      window.addEventListener('pagehide', () => this.flush(true));
    }

    track(name, amount = 1) {
      if (!this.endpoint || !this.anonKey || !this.analyticsAllowed()) return;
      const now = Date.now();
      if (!this.startedAt) {
        this.startedAt = now;
        this.lastActiveAt = now;
        this.lastFlushAt = now;
        this.scheduleFlush();
      } else if (this.lastActiveAt) {
        this.activeMilliseconds += Math.min(now - this.lastActiveAt, 15000);
        this.lastActiveAt = now;
      }
      if (Object.prototype.hasOwnProperty.call(this.counters, name)) {
        this.counters[name] += amount;
      }
      if (now - this.lastFlushAt >= 30000) this.flush(false);
    }

    scheduleFlush() {
      if (this.flushTimer) return;
      this.flushTimer = window.setInterval(() => this.flush(false), 30000);
    }

    payload(action = 'session') {
      const now = Date.now();
      const liveMilliseconds = this.lastActiveAt ? Math.min(now - this.lastActiveAt, 15000) : 0;
      const product = this.snapshot.product || {};
      return {
        action,
        session_id: this.sessionId,
        section_id: this.root.dataset.sectionId || '',
        product_id: this.root.dataset.productId || '',
        product_handle: this.root.dataset.productHandle || '',
        product_title: this.root.dataset.productTitle || product.name || '',
        product_model: product.model || '',
        page_url: window.location.href.slice(0, 1000),
        referrer: document.referrer.slice(0, 1000),
        started_at: this.startedAt ? new Date(this.startedAt).toISOString() : new Date().toISOString(),
        last_seen_at: new Date().toISOString(),
        active_seconds: Math.max(1, Math.round((this.activeMilliseconds + liveMilliseconds) / 1000)),
        counters: { ...this.counters },
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      };
    }

    async request(payload, keepalive = false) {
      if (!this.endpoint || !this.anonKey) return null;
      const response = await fetch(this.endpoint, {
        method: 'POST',
        keepalive,
        headers: {
          'Content-Type': 'application/json',
          apikey: this.anonKey,
          Authorization: `Bearer ${this.anonKey}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Request failed: ${response.status}`);
      return data;
    }

    async flush(keepalive) {
      if (!this.startedAt || !this.analyticsAllowed()) return;
      const now = Date.now();
      this.lastFlushAt = now;
      try {
        await this.request(this.payload(), keepalive);
      } catch (error) {
        console.warn('[LED mirror v2] analytics upload failed', error);
      }
    }

    async submitFeedback(event) {
      event.preventDefault();
      if (!this.feedbackForm?.reportValidity()) return;
      if (!this.endpoint || !this.anonKey) {
        this.feedbackStatus.textContent = 'Feedback is not configured yet. Please contact the store directly.';
        this.feedbackStatus.dataset.state = 'error';
        return;
      }
      const submit = this.feedbackForm.querySelector('[type="submit"]');
      const form = new FormData(this.feedbackForm);
      const feedback = String(form.get('feedback') || '').trim();
      const email = String(form.get('email') || '').trim();
      const consent = form.get('consent') === 'on';
      if (!feedback || !email || !consent) return;

      submit.disabled = true;
      this.feedbackStatus.textContent = 'Sending your feedback...';
      this.feedbackStatus.dataset.state = 'pending';
      try {
        const result = await this.request({
          ...this.payload('feedback'),
          feedback,
          email,
          consent: true
        });
        this.feedbackForm.reset();
        this.feedbackStatus.textContent = result?.message || 'Thank you for joining in. We will review your feedback and email you if it qualifies for a per-mirror discount.';
        this.feedbackStatus.dataset.state = 'success';
      } catch (error) {
        const message = String(error?.message || '');
        this.feedbackStatus.textContent = /requested function was not found|request failed: 404/i.test(message)
          ? 'Feedback service is not deployed yet. Please contact the store team.'
          : `We could not send your feedback: ${message || 'please try again.'}`;
        this.feedbackStatus.dataset.state = 'error';
        console.warn('[LED mirror v2] feedback failed', error);
      } finally {
        submit.disabled = false;
      }
    }

    destroy() {
      if (this.flushTimer) window.clearInterval(this.flushTimer);
      this.flushTimer = null;
      this.flush(true);
    }
  }

  class MirrorSnapshot {
    constructor(root, snapshot) {
      this.root = root;
      this.snapshot = snapshot;
      this.state = createState(snapshot);
      this.timers = new Set();
      this.radarIdleTimer = null;
      this.radarVisualTimer = null;
      this.press = null;
      this.lastPowerTapAt = null;
      this.tapCycleIndex = 0;
      this.rampTimer = null;
      this.rampDirection = 1;
      this.brightnessShout = null;
      this.sensorEnabledShout = false;
      this.service = { mode: null, step: 0, timeout: null, pulse: null };
      this.interactionCount = 0;
      this.selectedTask = 'radar';
      this.completedTasks = new Set();
      this.advanceTaskTimer = null;
      this.tracker = new EngagementTracker(root, snapshot);

      this.els = {
        stage: root.querySelector('[data-stage]'),
        mirrorWrap: root.querySelector('[data-mirror-wrap]'),
        mirror: root.querySelector('[data-mirror]'),
        heater: root.querySelector('.led-mirror-snapshot__heater'),
        sensorPulse: root.querySelector('.led-mirror-snapshot__sensor-pulse'),
        readout: root.querySelector('[data-readout]'),
        status: root.querySelector('[data-status]'),
        supply: root.querySelector('[data-supply]'),
        sensorMini: root.querySelector('[data-indicator-mini="sensor"]'),
        defogMini: root.querySelector('[data-indicator-mini="defog"]'),
        powerMini: root.querySelector('[data-indicator-mini="power"]'),
        sensorBig: root.querySelector('[data-indicator-big="sensor"]'),
        defogBig: root.querySelector('[data-indicator-big="defog"]'),
        powerBig: root.querySelector('[data-indicator-big="power"]'),
        sensorTimer: root.querySelector('[data-sensor-timer]'),
        defogButton: root.querySelector('[data-control="defog"]'),
        powerButton: root.querySelector('[data-control="power"]'),
        mirrorPrompt: root.querySelector('[data-mirror-prompt]'),
        stateShout: root.querySelector('[data-mirror-state-shout]'),
        featureTaskButtons: [...root.querySelectorAll('[data-feature-task]')],
        checklistProgress: root.querySelector('[data-checklist-progress]'),
        guideSelect: root.querySelector('[data-guide-select]'),
        guideText: root.querySelector('[data-guide-text]'),
        interactionCount: root.querySelector('[data-interaction-count]'),
        attribution: root.querySelector('[data-attribution]'),
        attributionBrand: root.querySelector('[data-attribution-brand]'),
        attributionLink: root.querySelector('[data-attribution-link]')
      };

      this.bind();
      this.updateGuide();
      this.configureAttribution();
      this.render();
      this.scheduleRadarIdle();
      this.startClock();
    }

    delay(callback, milliseconds) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        callback();
      }, milliseconds);
      this.timers.add(timer);
      return timer;
    }

    bind() {
      this.els.supply?.addEventListener('click', () => this.toggleSupply());
      this.bindPress(this.els.powerButton, 'power');
      this.bindPress(this.els.defogButton, 'defog');

      const radarInput = (event) => this.detectRadar(event);
      this.els.mirror?.addEventListener('pointermove', radarInput);
      this.els.mirror?.addEventListener('pointerenter', radarInput);
      this.els.mirror?.addEventListener('pointerleave', () => this.clearRadarVisual());
      this.els.mirror?.addEventListener('pointercancel', () => this.clearRadarVisual());
      this.els.mirror?.addEventListener('touchmove', radarInput, { passive: true });
      this.els.mirror?.addEventListener('touchend', () => this.clearRadarVisual(), { passive: true });
      this.els.guideSelect?.addEventListener('change', () => this.updateGuide());
      this.els.featureTaskButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.selectedTask = button.dataset.featureTask || 'radar';
          this.updateGuide();
        });
      });

      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target?.contains(this.root)) this.destroy();
      });
    }

    renderMirrorPrompt(selectedGuide) {
      if (!this.els.mirrorPrompt) return;
      if (selectedGuide === 'sensor-disable' || selectedGuide === 'sensor-restore') {
        let promptKey = selectedGuide;
        if (this.service.step === 1) {
          promptKey = selectedGuide === 'sensor-restore' ? 'sensor-restore-setting' : 'sensor-setting';
        } else if (this.service.step === 2) {
          if (selectedGuide === 'sensor-disable') {
            promptKey = 'sensor-disable-confirm';
          } else {
            promptKey = this.service.mode === 'enable' ? 'sensor-restore-confirm' : 'sensor-restore-tap';
          }
        }
        this.setMirrorPromptText(MIRROR_PROMPTS[promptKey] || MIRROR_PROMPTS[selectedGuide]);
        return;
      }
      if (selectedGuide !== 'brightness') {
        this.setMirrorPromptText(MIRROR_PROMPTS[selectedGuide] || MIRROR_PROMPTS.supply);
        return;
      }

      const on = document.createElement('span');
      on.className = 'led-mirror-snapshot__prompt-hot';
      on.textContent = 'ON';
      this.els.mirrorPrompt.replaceChildren('when LED ', on, ', long hold power');
      this.scaleMirrorPrompt('when LED ON, long hold power');
    }

    setMirrorPromptText(text) {
      this.els.mirrorPrompt.textContent = text;
      this.scaleMirrorPrompt(text);
    }

    scaleMirrorPrompt(text) {
      const length = String(text || '').length;
      const size = Math.max(18, Math.min(34, 36 - Math.max(0, length - 20) * 0.42));
      this.els.mirrorPrompt.style.fontSize = `${size}px`;
    }

    updateGuide() {
      const selectedGuide = this.selectedTask || this.els.guideSelect?.value || 'radar';
      this.renderMirrorPrompt(selectedGuide);
      this.els.featureTaskButtons.forEach((button) => {
        const key = button.dataset.featureTask || '';
        const done = this.completedTasks.has(key);
        button.classList.toggle('is-active', key === selectedGuide);
        button.classList.toggle('is-complete', done);
        const badge = button.querySelector('.led-mirror-snapshot__feature-check');
        if (badge) badge.textContent = done ? 'OK' : String(FEATURE_TASK_KEYS.indexOf(key) + 1).padStart(2, '0');
      });
      if (this.els.checklistProgress) {
        this.els.checklistProgress.textContent = `${this.completedTasks.size}/${FEATURE_TASKS.length} done`;
      }
      if (!this.els.guideText) return;
      const parts = GUIDE_COPY[selectedGuide] || GUIDE_COPY.supply;
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (typeof part === 'string') {
          fragment.append(document.createTextNode(part));
          return;
        }
        const highlight = document.createElement('strong');
        highlight.className = `led-mirror-snapshot__guide-keyword is-${part.tone}`;
        highlight.textContent = part.text;
        fragment.append(highlight);
      });
      this.els.guideText.replaceChildren(fragment);
    }

    completeTask(key) {
      if (!FEATURE_TASK_BY_KEY[key] || this.completedTasks.has(key)) return;
      this.completedTasks.add(key);
      const counter = FEATURE_TASK_BY_KEY[key].completedCounter;
      if (counter) this.tracker.track(counter);
      this.updateGuide();
      if (this.advanceTaskTimer) window.clearTimeout(this.advanceTaskTimer);
      const advanceDelay = key === 'colour' ? 4200 : 900;
      this.advanceTaskTimer = this.delay(() => {
        const currentIndex = FEATURE_TASK_KEYS.indexOf(key);
        const nextInSequence = FEATURE_TASK_KEYS
          .slice(currentIndex + 1)
          .find((taskKey) => !this.completedTasks.has(taskKey));
        const next = nextInSequence || FEATURE_TASK_KEYS.find((taskKey) => !this.completedTasks.has(taskKey));
        if (!next) return;
        this.selectedTask = next;
        this.updateGuide();
      }, advanceDelay);
    }

    recordInteraction() {
      this.interactionCount += 1;
      if (this.els.interactionCount) {
        this.els.interactionCount.textContent = String(this.interactionCount).padStart(2, '0');
      }
    }

    bindPress(element, control) {
      if (!element) return;

      const start = (event) => {
        if (!this.state.supplyOn || this.press) return;
        event.preventDefault();
        element.setPointerCapture?.(event.pointerId);
        this.press = {
          control,
          startedAt: performance.now(),
          consumed: false,
          brightnessStarted: false,
          timers: []
        };

        if (control === 'power') {
          this.press.timers.push(this.delay(() => {
            if (!this.press || this.press.control !== 'power' || !this.state.lightOn) return;
            this.press.consumed = true;
            this.press.brightnessStarted = true;
            this.startBrightnessRamp();
          }, this.snapshot.timing.brightnessHoldSeconds * 1000));

          this.press.timers.push(this.delay(() => {
            if (!this.press || this.press.control !== 'power' || this.state.lightOn) return;
            this.press.consumed = true;
            this.handleServiceHold();
          }, this.snapshot.timing.serviceHoldSeconds * 1000));
        }
      };

      const end = (event) => {
        if (!this.press || this.press.control !== control) return;
        event.preventDefault();
        const activePress = this.press;
        this.press = null;
        activePress.timers.forEach((timer) => {
          window.clearTimeout(timer);
          this.timers.delete(timer);
        });

        if (activePress.brightnessStarted) {
          this.stopBrightnessRamp(true);
          return;
        }
        if (activePress.consumed) return;
        if (control === 'power') this.tapPower();
        if (control === 'defog') this.tapDefog();
      };

      element.addEventListener('pointerdown', start);
      element.addEventListener('pointerup', end);
      element.addEventListener('pointercancel', end);
      element.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        control === 'power' ? this.tapPower() : this.tapDefog();
      });
    }

    runBoot() {
      if (!this.state.supplyOn) return;
      this.clearService();
      this.state.booting = true;
      this.state.lightOn = true;
      this.state.brightness = this.snapshot.light.maximumBrightness;
      this.state.cct = 'neutral';
      this.state.defogOn = false;
      this.state.radarMotion = false;
      this.state.indicators.power = 'white';
      this.state.indicators.defog = 'white';
      this.state.indicators.sensor = this.state.sensorMode === 'permanent' ? 'off' : 'white';
      this.render();

      this.delay(() => {
        if (!this.state.supplyOn) return;
        this.state.booting = false;
        this.state.lightOn = false;
        this.state.brightness = this.state.memory.brightness;
        this.state.cct = this.state.memory.cct;
        this.state.indicators.power = 'white';
        this.state.indicators.defog = 'white';
        this.state.indicators.sensor = this.state.sensorMode === 'permanent' ? 'off' : 'white';
        this.render();
        this.scheduleRadarIdle();
      }, this.snapshot.timing.bootSeconds * 1000);
    }

    toggleSupply() {
      this.recordInteraction();
      this.tracker.track('supply_toggles');
      this.state.supplyOn = !this.state.supplyOn;
      this.clearRuntimeTimers();

      if (!this.state.supplyOn) {
        this.state.booting = false;
        this.state.lightOn = false;
        this.state.defogOn = false;
        this.state.radarMotion = false;
        this.state.indicators = { sensor: 'off', defog: 'off', power: 'off' };
        this.render();
        return;
      }

      this.runBoot();
    }

    tapPower() {
      if (!this.state.supplyOn || this.state.booting) return;
      const serviceTapPending = Boolean(this.service.mode);
      this.recordInteraction();
      this.tracker.track('power_taps');
      if (!serviceTapPending) this.completeTask('power');
      if (this.handleServiceTap()) {
        if (serviceTapPending && this.advanceTaskTimer) {
          window.clearTimeout(this.advanceTaskTimer);
          this.advanceTaskTimer = null;
        }
        this.updateGuide();
        return;
      }
      this.sensorEnabledShout = false;

      const now = performance.now();
      // The interval alone determines colour cycling, including its unlit step.
      const inCycle = this.lastPowerTapAt !== null
        && now - this.lastPowerTapAt <= this.snapshot.timing.tapCycleWindowSeconds * 1000;
      this.lastPowerTapAt = now;
      const wasSensorPaused = this.state.sensorMode === 'temporary';

      if (!inCycle) {
        this.tapCycleIndex = 0;
        if (wasSensorPaused) {
          this.state.sensorMode = 'enabled';
          this.state.indicators.sensor = 'white';
        }
        if (this.state.lightOn) {
          this.state.lightOn = false;
          this.state.indicators.power = 'white';
        } else {
          this.readMemory();
          this.state.lightOn = true;
          this.state.indicators.power = 'off';
        }
        this.render();
        return;
      }

      const cycle = this.snapshot.light.cctCycle;
      this.tapCycleIndex = (this.tapCycleIndex + 1) % cycle.length;
      const selection = cycle[this.tapCycleIndex];
      this.tracker.track('colour_changes');
      this.completeTask('colour');
      if (wasSensorPaused) {
        this.state.sensorMode = 'enabled';
        this.state.indicators.sensor = 'white';
      }
      if (selection === 'off') {
        this.state.lightOn = false;
        this.state.indicators.power = 'white';
      } else {
        this.state.lightOn = true;
        this.state.cct = selection;
        this.state.indicators.power = 'off';
        this.writeMemory();
      }
      this.render();
    }

    tapDefog() {
      if (!this.state.supplyOn || this.state.booting) return;
      this.recordInteraction();
      this.tracker.track('defog_taps');
      this.completeTask('defog');
      this.state.defogOn = !this.state.defogOn;
      this.state.indicators.defog = this.state.defogOn ? 'off' : 'white';
      this.render();
    }

    startBrightnessRamp() {
      this.recordInteraction();
      this.stopBrightnessRamp(false);
      this.brightnessShout = this.rampDirection > 0 ? 'brighter' : 'dimmer';
      const min = this.snapshot.light.minimumBrightness;
      const max = this.snapshot.light.maximumBrightness;
      const step = this.rampDirection > 0 ? 2.5 : -2.5;

      const update = () => {
        this.state.brightness = clamp(this.state.brightness + step, min, max);
        this.render();
      };
      update();
      this.rampTimer = window.setInterval(update, 67.5);
    }

    stopBrightnessRamp(commit) {
      if (this.rampTimer) window.clearInterval(this.rampTimer);
      this.rampTimer = null;
      this.brightnessShout = null;
      if (!commit) return;
      this.tracker.track('brightness_adjustments');
      this.completeTask('brightness');
      this.writeMemory();
      this.rampDirection *= -1;
      this.render();
    }

    writeMemory() {
      this.state.memory.brightness = this.state.brightness;
      this.state.memory.cct = this.state.cct;
    }

    readMemory() {
      this.state.brightness = this.state.memory.brightness;
      this.state.cct = this.state.memory.cct;
    }

    detectRadar() {
      if (!this.state.supplyOn
        || this.state.booting
        || this.state.sensorMode !== 'enabled'
        || this.service.mode) return;

      if (!this.state.radarMotion) {
        this.recordInteraction();
        this.tracker.track('sensor_triggers');
        this.completeTask('radar');
      }

      this.state.radarMotion = true;
      if (!this.state.lightOn) {
        this.readMemory();
        this.state.lightOn = true;
        this.state.indicators.power = 'off';
      }
      this.render();

      if (this.radarVisualTimer) window.clearTimeout(this.radarVisualTimer);
      this.radarVisualTimer = this.delay(() => {
        this.radarVisualTimer = null;
        this.state.radarMotion = false;
        this.render();
      }, 650);
      this.scheduleRadarIdle();
    }

    clearRadarVisual() {
      if (this.radarVisualTimer) {
        window.clearTimeout(this.radarVisualTimer);
        this.timers.delete(this.radarVisualTimer);
      }
      this.radarVisualTimer = null;
      if (!this.state.radarMotion) return;
      this.state.radarMotion = false;
      this.render();
    }

    scheduleRadarIdle() {
      if (this.radarIdleTimer) window.clearTimeout(this.radarIdleTimer);
      if (this.state.sensorMode !== 'enabled' || !this.state.supplyOn) return;
      this.radarIdleStartedAt = performance.now();
      this.radarIdleTimer = this.delay(() => {
        this.radarIdleTimer = null;
        if (this.state.sensorMode !== 'enabled' || this.state.radarMotion) return;
        this.state.lightOn = false;
        this.state.indicators.power = 'white';
        this.render();
      }, this.snapshot.timing.radarIdleSeconds * 1000);
    }

    handleServiceHold() {
      if (this.state.lightOn || !this.state.supplyOn) return;
      this.recordInteraction();

      if (!this.service.mode) {
        this.tracker.track('service_starts');
        this.service.mode = 'pause';
        this.service.step = 1;
        this.state.sensorMode = 'temporary';
        this.state.radarMotion = false;
        this.state.indicators.sensor = 'off';
        this.state.indicators.power = 'off';
        this.scheduleServiceTimeout();
        this.updateGuide();
        this.render();
        return;
      }

      if (this.service.step === 1) {
        this.service.mode = 'disable';
        this.service.step = 2;
        this.state.indicators.power = 'blueFlash';
        this.scheduleServiceTimeout();
        this.updateGuide();
        this.render();
        return;
      }

      if (this.service.step === 2) {
        if (this.service.mode === 'enable') {
          this.state.sensorMode = 'enabled';
          this.state.indicators.sensor = 'white';
          this.state.indicators.power = 'white';
          this.tracker.track('service_actions');
          this.tracker.track('radar_reactivate_actions');
          this.completeTask('sensor-restore');
          this.sensorEnabledShout = true;
          this.clearService();
          this.render();
          return;
        }

        this.state.sensorMode = 'permanent';
        this.state.indicators.sensor = 'off';
        this.state.indicators.power = 'white';
        this.tracker.track('service_actions');
        this.tracker.track('radar_disable_actions');
        this.completeTask('sensor-disable');
        this.clearService();
        this.render();
        return;
      }

    }

    handleServiceTap() {
      if (!this.service.mode) return false;
      if (this.service.step === 1) {
        this.clearService();
        return false;
      }
      if (this.service.step === 2) {
        this.service.mode = 'enable';
        this.state.indicators.power = 'white';
        this.updateGuide();
        this.render();
        this.service.pulse = this.delay(() => {
          if (this.service.mode === 'enable' && this.service.step === 2) {
            this.state.indicators.power = 'blueFlash';
            this.render();
          }
        }, 160);
        this.scheduleServiceTimeout();
      }
      return true;
    }

    scheduleServiceTimeout() {
      if (this.service.timeout) window.clearTimeout(this.service.timeout);
      this.service.timeout = this.delay(() => {
        this.state.sensorMode = 'temporary';
        this.state.indicators.sensor = 'off';
        this.state.indicators.power = 'off';
        this.clearService();
        this.render();
      }, this.snapshot.timing.serviceStepTimeoutSeconds * 1000);
    }

    clearService() {
      if (this.service.timeout) window.clearTimeout(this.service.timeout);
      if (this.service.pulse) window.clearTimeout(this.service.pulse);
      this.service = { mode: null, step: 0, timeout: null, pulse: null };
    }

    clearRuntimeTimers() {
      this.stopBrightnessRamp(false);
      this.clearService();
      if (this.radarIdleTimer) window.clearTimeout(this.radarIdleTimer);
      if (this.radarVisualTimer) window.clearTimeout(this.radarVisualTimer);
      this.radarIdleTimer = null;
      this.radarVisualTimer = null;
    }

    configureAttribution() {
      const attribution = this.snapshot.attribution || {};
      const dataDomains = this.root.dataset.officialDomains || '';
      const allowed = [
        ...(attribution.allowedDomains || []),
        ...dataDomains.split(',')
      ].map(normalizeHost).filter(Boolean);
      const host = normalizeHost(window.location.hostname);
      const official = allowed.some((domain) => host === domain || host.endsWith(`.${domain}`));
      const test = isTestEnvironment();

      if (official || test || !this.els.attribution) return;

      const storeUrl = this.root.dataset.officialStoreUrl || attribution.storeUrl || '#';
      const brand = this.root.dataset.brand || attribution.brand || 'Official store';
      this.els.attributionBrand.textContent = brand;
      this.els.attributionLink.href = storeUrl;
      this.els.attribution.hidden = false;
    }

    statusText() {
      if (!this.state.supplyOn) return 'POWER OFF';
      if (this.state.booting) return 'INITIALIZING';
      if (this.state.sensorMode === 'permanent') return 'SENSOR OFF';
      if (this.service.step === 2) return 'SETTING MODE';
      if (this.state.sensorMode === 'temporary') return 'SENSOR PAUSED';
      if (this.state.radarMotion) return 'MOTION';
      return 'READY';
    }

    renderIndicator(name, mode) {
      const color = mode.startsWith('blue') ? '#54a7ff' : '#ffffff';
      const on = mode !== 'off' && this.state.supplyOn;
      const flash = mode.endsWith('Flash');
      [this.els[`${name}Mini`], this.els[`${name}Big`]].forEach((element) => {
        if (!element) return;
        element.classList.toggle('is-on', on);
        element.classList.toggle('is-flashing', on && flash);
        element.style.setProperty('--indicator-color', color);
      });
    }

    render() {
      const state = this.state;
      this.root.style.setProperty('--mirror-cct', CCT_COLORS[state.cct] || CCT_COLORS.neutral);
      this.root.style.setProperty('--mirror-strength', String(Math.max(0.12, state.brightness / 100)));
      this.root.style.setProperty('--mirror-glow', String(Math.max(0.18, state.brightness / 100)));
      this.root.classList.toggle('is-light-on', state.supplyOn && state.lightOn);
      this.root.classList.toggle('is-power-off', !state.supplyOn);
      this.root.classList.toggle('is-defog-on', state.supplyOn && state.defogOn);
      this.root.classList.toggle('is-radar-active', state.radarMotion);
      this.root.classList.toggle('is-state-shout-visible', state.supplyOn && !state.booting);
      if (this.els.heater) {
        this.els.heater.classList.toggle('is-active', state.supplyOn && state.defogOn);
        this.els.heater.style.display = 'block';
        this.els.heater.style.opacity = state.supplyOn && state.defogOn ? '1' : '0';
      }
      if (this.els.sensorPulse) {
        this.els.sensorPulse.classList.toggle('is-active', state.radarMotion);
        this.els.sensorPulse.style.display = 'block';
        this.els.sensorPulse.style.visibility = 'visible';
      }
      this.els.supply?.classList.toggle('is-off', !state.supplyOn);
      this.els.supply?.setAttribute('aria-pressed', state.supplyOn ? 'true' : 'false');
      this.els.status.textContent = this.statusText();
      this.els.readout.textContent = state.lightOn
        ? `${Math.round(state.brightness / this.snapshot.light.maximumBrightness * 100)}% / ${state.cct.toUpperCase()}`
        : 'LED OFF';
      if (this.els.stateShout) {
        const shoutKey = this.service.step === 2
          ? 'setting-mode'
          : this.sensorEnabledShout
          ? 'sensor-enabled'
          : state.sensorMode === 'permanent'
          ? 'sensor-disabled'
          : state.sensorMode === 'temporary'
            ? 'sensor-paused'
            : state.lightOn
            ? (this.brightnessShout || state.cct)
            : 'off';
        const shoutText = shoutKey === 'setting-mode'
          ? 'SETTING MODE'
          : shoutKey === 'sensor-enabled'
          ? "SENSOR ENABLED,Don't forget to tap power once to activat it"
          : shoutKey === 'sensor-disabled'
          ? 'SENSOR DISABLED'
          : shoutKey === 'sensor-paused'
          ? 'SENSOR PAUSED'
          : shoutKey === 'cool'
            ? 'COLD'
            : shoutKey.toUpperCase();
        const shoutColor = shoutKey === 'brighter' || shoutKey === 'dimmer'
          ? (CCT_COLORS[state.cct] || CCT_COLORS.neutral)
          : shoutKey === 'warm'
          ? '242, 196, 135'
          : shoutKey === 'cool'
            ? '179, 213, 247'
            : shoutKey === 'setting-mode'
              ? '84, 167, 255'
            : shoutKey === 'sensor-enabled'
              ? '180, 230, 185'
            : shoutKey === 'sensor-paused'
              ? '154, 205, 255'
              : shoutKey === 'sensor-disabled'
                ? '255, 154, 104'
            : shoutKey === 'off'
              ? '118, 128, 142'
              : '255, 255, 255';
        this.els.stateShout.textContent = shoutText;
        this.els.stateShout.dataset.state = shoutKey;
        this.els.stateShout.style.setProperty('--state-shout-color', shoutColor);
      }

      this.renderIndicator('sensor', state.indicators.sensor);
      this.renderIndicator('defog', state.indicators.defog);
      this.renderIndicator('power', state.indicators.power);
    }

    startClock() {
      const tick = () => {
        if (!this.root.isConnected) return;
        if (this.els.sensorTimer) {
          const elapsed = this.radarIdleStartedAt
            ? Math.floor((performance.now() - this.radarIdleStartedAt) / 1000)
            : 0;
          this.els.sensorTimer.textContent = String(clamp(elapsed, 0, 59)).padStart(2, '0');
        }
        window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    }

    destroy() {
      this.tracker.destroy();
      this.clearRuntimeTimers();
      if (this.advanceTaskTimer) window.clearTimeout(this.advanceTaskTimer);
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers.clear();
    }
  }

  async function loadSnapshot(root) {
    const url = root.dataset.snapshotUrl;
    if (!url) return DEFAULT_SNAPSHOT;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Snapshot request failed: ${response.status}`);
    return {
      ...DEFAULT_SNAPSHOT,
      ...await response.json()
    };
  }

  async function initialize(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    configureProductPlacement(root);
    try {
      const snapshot = await loadSnapshot(root);
      new MirrorSnapshot(root, snapshot);
    } catch (error) {
      console.warn('[LED mirror snapshot] using built-in snapshot fallback', error);
      new MirrorSnapshot(root, DEFAULT_SNAPSHOT);
    }
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-led-mirror-snapshot]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
